import Image from "next/image";
import Link from "next/link";

import {
  motion,
  useMotionTemplate,
  useMotionValue,
  useMotionValueEvent,
  useSpring,
  useTransform,
} from "motion/react";

import { useWindowSize } from "@react-hookz/web";
import { documents } from "data/investors";
import {
  boardOfAdvisorsAndDirectors,
  executiveManagement,
} from "data/leadership";
import { useLenis } from "lenis/react";

import {
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenu as NavigationMenuPrimitive,
  NavigationMenuTrigger,
} from "components/NavigationMenu";

import MoreLogoImage from "/images/common/more-logo.svg";
import CookbookLogoImage from "/images/cookbook/header/cookbook-wordmark-colouredonwhite.svg";

import ArrowRightAltIcon from "@material-symbols/svg-700/sharp/arrow_right_alt.svg";

interface NavigationMenuProps {
  items: Array<"find-a-store" | "about" | "cookbook" | "investors">;
  overlay?: boolean;
}

function NavigationMenu({ items, overlay = false }: NavigationMenuProps) {
  const { height: windowHeight } = useWindowSize();
  const scrollPosition = useMotionValue(0);

  const rawNavigationMenuBlur = useMotionValue(0);
  const navigationMenuBlur = useSpring(rawNavigationMenuBlur, {});
  const navigationMenuBackdropFilter = useMotionTemplate`blur(${navigationMenuBlur}px)`;

  const navigationMenuBackgroundColor = useTransform(
    scrollPosition,
    [windowHeight - 100, windowHeight],
    ["#ffffff00", "#ffffffbf"],
  );

  useLenis((lenis) => {
    scrollPosition.set(lenis.animatedScroll);
  });

  useMotionValueEvent(scrollPosition, "change", (scrollPosition) => {
    if (scrollPosition > 24) rawNavigationMenuBlur.set(24);
    else rawNavigationMenuBlur.set(0);
  });
  return (
    <NavigationMenuPrimitive
      className={`sticky z-50 mx-auto max-md:hidden ${overlay ? "top-9 -mt-17" : "top-7 -mt-23 mb-7"}`}
      // value="investors"
    >
      <motion.div
        className="flex h-17 gap-3 rounded-full border-none"
        style={{
          backdropFilter: navigationMenuBackdropFilter,
          WebkitBackdropFilter: navigationMenuBackdropFilter,
          backgroundColor: overlay
            ? navigationMenuBackgroundColor
            : "#ffffffbf",
        }}
        initial={{
          opacity: 0,
        }}
        animate={{
          opacity: 1,
        }}
      >
        <NavigationMenuList className="size-full">
          {items.includes("cookbook") && (
            <NavigationMenuItem value="cookbook">
              <NavigationMenuTrigger className="typography-para-20 text-white mix-blend-difference">
                cookbook
              </NavigationMenuTrigger>
              <NavigationMenuContent className="shadow-3xl flex h-80 w-160 gap-3 rounded-3xl bg-white p-3">
                <NavigationMenuLink
                  className="group flex min-h-full w-55 grow flex-col items-start gap-4 rounded-2xl bg-orange-60 p-3 transition-[background-color] duration-300 hover:bg-orange-70"
                  asChild
                >
                  <Link href="/cookbook" passHref>
                    <CookbookLogoImage className="h-12" />
                    <div className="flex h-full flex-col gap-3">
                      <p className="typography-para-30 text-white">
                        Our perspectives on retail, innovation, tech and people.
                      </p>
                      <p className="typography-para-30 text-white">
                        Discover how we cook.
                      </p>
                    </div>
                    <div className="flex h-12 min-h-12 w-full items-center rounded-full bg-white px-4 typography-label-thick-30 text-orange-60">
                      Read
                    </div>
                  </Link>
                </NavigationMenuLink>
                <div className="flex flex-col gap-3">
                  <h1 className="typography-head-50 text-neutral-110">
                    <span className="typography-head-pop-50 font-normal italic">
                      trending
                    </span>{" "}
                    recipes
                  </h1>
                  <div className="flex grow gap-3">
                    <motion.span
                      initial={{ opacity: 0, y: 20 }}
                      animate={{
                        opacity: 1,
                        y: 0,
                        transition: { duration: 0.2, delay: 0.2 },
                      }}
                    >
                      <NavigationMenuLink asChild>
                        <Link
                          className="group flex basis-1/2 flex-col gap-3 duration-300"
                          href="/cookbook/recipe/beyond-metros-the-bharat-opportunity"
                        >
                          <div className="relative h-28 min-w-50 overflow-hidden rounded-xl bg-neutral-100">
                            <img
                              className="transition-transform duration-300 group-hover:scale-103"
                              src="/images/cookbook/thumbnails/beyond-metros-the-bharat-opportunity.jpg"
                              alt=""
                              // fill
                            />
                          </div>
                          <h1 className="typography-para-thick-20 text-neutral-110">
                            Beyond Metros: How More Retail is Unlocking the True
                            Potential of Bharat
                          </h1>
                          <ArrowRightAltIcon className="size-4 fill-neutral-110 transition-[fill] duration-300 group-hover:fill-orange-60" />
                        </Link>
                      </NavigationMenuLink>
                    </motion.span>
                    <motion.span
                      initial={{ opacity: 0, y: 20 }}
                      animate={{
                        opacity: 1,
                        y: 0,
                        transition: { duration: 0.2, delay: 0.4 },
                      }}
                    >
                      <NavigationMenuLink asChild>
                        <Link
                          className="group flex basis-1/2 flex-col gap-3 duration-300"
                          href="/cookbook/recipe/more-than-work-a-day-at-area-83"
                        >
                          <div className="relative h-28 min-w-50 overflow-hidden rounded-xl bg-neutral-100">
                            <Image
                              className="transition-transform duration-300 group-hover:scale-103"
                              src="/images/cookbook/thumbnails/more-than-work-a-day-at-area-83.jpg"
                              alt=""
                              fill
                            />
                          </div>
                          <h1 className="typography-para-thick-20 text-neutral-110">
                            More Than Work, A Day at Area 83
                          </h1>
                          <ArrowRightAltIcon className="size-4 fill-neutral-110 transition-[fill] duration-300 group-hover:fill-orange-60" />
                        </Link>
                      </NavigationMenuLink>
                    </motion.span>
                  </div>
                </div>
              </NavigationMenuContent>
            </NavigationMenuItem>
          )}
          {items.includes("about") && (
            <NavigationMenuItem value="about">
              <NavigationMenuTrigger className="typography-para-20 text-white mix-blend-difference">
                about
              </NavigationMenuTrigger>
              <NavigationMenuContent className="shadow-3xl flex h-80 w-160 min-w-160 gap-3 rounded-3xl bg-white p-3 shadow-neutral-110/15">
                <NavigationMenuLink>
                  <Link
                    href="/history"
                    passHref
                    className="group bg-black-110 relative flex w-55 grow flex-col items-start justify-between overflow-hidden rounded-xl"
                  >
                    <Image
                      className="absolute size-full object-cover transition-transform duration-300 group-hover:scale-102"
                      src="/images/common/navigation-menu/nav-about.jpg"
                      alt="A more supermarket"
                      width={132}
                      height={308}
                    />
                    <div className="z-60 flex size-full flex-col items-start justify-between bg-black/25 p-3 transition-[background-color] duration-300 group-hover:bg-black/50">
                      <MoreLogoImage className="h-4 fill-white" />
                      <div className="flex flex-col gap-3">
                        <p className="h-min typography-para-10 overflow-ellipsis text-white drop-shadow-2xl">
                          <span className="typography-label-10 font-black">
                            more
                          </span>{" "}
                          is
                          <br />
                          <span className="typography-para-pop-thick-10 font-black [font-variation-settings:'SOFT'_100]">
                            your
                          </span>{" "}
                          supermarket
                        </p>
                        <p className="typography-para-30 text-white">
                          Explore our legacy of trust - see how{" "}
                          <span className="typography-label-30 font-black">
                            more
                          </span>{" "}
                          has grown with you over the years.
                        </p>
                        <ArrowRightAltIcon className="size-4 fill-white" />
                      </div>
                    </div>
                  </Link>
                </NavigationMenuLink>
                <div className="flex grow flex-col gap-3">
                  <motion.span
                    className="size-full"
                    initial={{ opacity: 0 }}
                    animate={{
                      opacity: 1,
                      transition: { duration: 0.3, delay: 0.2 },
                    }}
                  >
                    <NavigationMenuLink asChild>
                      <Link
                        className="group relative size-full overflow-hidden rounded-xl bg-neutral-110"
                        href="/values"
                        passHref
                      >
                        <Image
                          className="absolute inset-0 size-full object-cover object-top transition-transform duration-300 group-hover:scale-102"
                          src="/images/common/navigation-menu/nav-value-tree.jpg"
                          alt="Values Tree"
                          width={1152}
                          height={393}
                        />
                        <div className="absolute inset-0 z-10 flex size-full flex-col justify-between bg-black/25 p-3 transition-[background-color] duration-300 group-hover:bg-black/50">
                          <div className="flex items-center justify-between gap-3">
                            <h1 className="typography-para-thick-20 text-white">
                              Values
                            </h1>
                            <ArrowRightAltIcon className="size-4 fill-white" />
                          </div>
                          <p className="typography-para-30 text-white">
                            Our values show up in every detail, earning your
                            trust in choosing{" "}
                            <span className="typgraphy-label-20 font-black">
                              more
                            </span>
                            .
                          </p>
                        </div>
                      </Link>
                    </NavigationMenuLink>
                  </motion.span>
                  <motion.span
                    initial={{ opacity: 0 }}
                    animate={{
                      opacity: 1,
                      transition: { duration: 0.3, delay: 0.4 },
                    }}
                  >
                    <NavigationMenuLink asChild>
                      <Link
                        className="group flex flex-col gap-3 rounded-xl bg-neutral-10 p-3 transition-[background-color] duration-300 hover:bg-neutral-20"
                        href="/leadership"
                        passHref
                      >
                        <div className="flex items-center justify-between">
                          <h1 className="typography-para-thick-20 text-neutral-110">
                            Leadership
                          </h1>
                          <ArrowRightAltIcon className="size-4 fill-neutral-110" />
                        </div>
                        <div className="flex flex-wrap">
                          {boardOfAdvisorsAndDirectors.map((leader, index) => (
                            <Image
                              key={index}
                              className="size-6 rounded-md object-cover transition-[border-radius] duration-300 group-hover:rounded-2xl"
                              src={leader.photoSrc}
                              alt={`A photo of a member of the board of advisors and directors,  ${leader.name}.`}
                              width={750}
                              height={750}
                              unselectable="on"
                              priority
                            />
                          ))}
                          {executiveManagement
                            .filter((leader) => leader.name !== "Vinod Nambiar")
                            .map((leader, index) => (
                              <Image
                                key={index}
                                className="size-6 rounded-md object-cover transition-[border-radius] duration-300 group-hover:rounded-2xl"
                                src={leader.photoSrc}
                                alt={`A photo of a member of the executive management,  ${leader.name}.`}
                                width={750}
                                height={750}
                                unselectable="on"
                                priority
                              />
                            ))}
                        </div>
                        <p className="typography-para-30 text-neutral-110">
                          The minds behind{" "}
                          <span className="typgraphy-label-30 font-black">
                            more
                          </span>{" "}
                          leading with integrity, purpose, and a focus on you.
                        </p>
                      </Link>
                    </NavigationMenuLink>
                  </motion.span>
                </div>
              </NavigationMenuContent>
            </NavigationMenuItem>
          )}
          {items.includes("investors") && (
            <NavigationMenuItem value="investors">
              <NavigationMenuTrigger className="typography-para-20 text-white mix-blend-difference">
                investors
              </NavigationMenuTrigger>
              <NavigationMenuContent className="shadow-3xl flex h-80 min-w-160 gap-3 rounded-3xl bg-white p-3">
                <NavigationMenuLink asChild>
                  <Link
                    className="group flex min-h-full w-55 min-w-55 grow flex-col items-start gap-4 rounded-2xl bg-neutral-100 p-3 transition-[background-color] duration-300 hover:bg-neutral-110"
                    href="/investors"
                  >
                    <div className="flex h-full flex-col gap-3">
                      <h1 className="typography-head-50 text-white">
                        investors
                      </h1>
                      <p className="typography-para-30 text-white">
                        Get a clear view into{" "}
                        <span className="typography-label-30 font-black">
                          more
                        </span>
                        ’s growth. accountability and transparency
                      </p>
                    </div>
                    <div className="flex h-12 min-h-12 w-full items-center rounded-full bg-orange-60 px-4 typography-label-thick-30 text-white shadow-xl shadow-orange-60/25 transition-[box-shadow] duration-300 group-hover:shadow-2xl">
                      Explore
                    </div>
                  </Link>
                </NavigationMenuLink>
                <div className="flex grow flex-col gap-3 rounded-2xl border border-neutral-20 p-3">
                  <div className="flex flex-col gap-3">
                    <h1 className="typography-head-50 text-neutral-110">
                      FY{documents[0].fiscalyear}
                    </h1>
                    <p className="typography-para-30 text-neutral-110">
                      Stay informed with{" "}
                      <span className="typography-label-30 font-black">
                        more
                      </span>
                      ’s latest available financial documents.
                    </p>
                  </div>
                  <div className="mt-3 flex grow gap-3 overflow-hidden rounded-xl bg-gradient-to-b from-neutral-10 from-50% to-neutral-30 p-3 pb-0">
                    {documents[0].documents.map((document, index) => (
                      <motion.div
                        key={index}
                        className="group relative flex size-full items-center justify-center overflow-hidden"
                        initial={{ opacity: 0, y: 50 }}
                        animate={{
                          opacity: 1,
                          y: 0,
                          transition: { duration: 0.2, delay: index * 0.2 },
                        }}
                      >
                        <div className="translate-y-10 transition-transform duration-300 group-hover:translate-y-8 sm:translate-y-9">
                          <Image
                            className="drop-shadow-md drop-shadow-neutral-110/50 md:drop-shadow-neutral-110"
                            src={document.cover.src}
                            alt="cover"
                            width={500}
                            height={650}
                          />
                        </div>
                        <a
                          className="absolute inset-0 z-10 size-full"
                          href={document.url}
                          target="_blank"
                        />
                      </motion.div>
                    ))}
                  </div>
                </div>
              </NavigationMenuContent>
            </NavigationMenuItem>
          )}
        </NavigationMenuList>
      </motion.div>
    </NavigationMenuPrimitive>
  );
}

export { NavigationMenu };
